import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { useShell } from '../../shell/ShellContext';
import { ShellRail, useShellUI } from '../../shell/AppShell';
import { leagueMeta, formatTeamRecord } from '../../shell/leagueUtils';
import { orphanMarketplaceAPI, startupMarketplaceAPI, rateMyTradeAPI } from '../../utils/api';
import { SleeperAvatar } from '../../shell/SleeperAvatar';

/**
 * ListTeamWizard — shell-native "List a team" flow at /orphan-marketplace/list.
 * Three steps (league → seat → terms) with a live listing preview in the rail,
 * per the redesign mock. Sleeper fills in roster/format; the lister sets terms.
 * Replaces the legacy OrphanListingForm surface (its create API contract kept:
 * sleeperUsername, sleeperLeagueId, sleeperRosterId, buyIn, yearsPaid,
 * escrowType, escrowDetails, description).
 */

/* Backend enum: leaguesafe | teamstake | other | null */
const ESCROWS = [
  ['LeagueSafe', 'leaguesafe'],
  ['TeamStake', 'teamstake'],
  ['Other', 'other'],
  ['None', null],
];
const THIS_SEASON = new Date().getFullYear();
const YEAR_OPTIONS = [THIS_SEASON, THIS_SEASON + 1];

const Stepper = ({ stage, steps }) => {
  const ix = steps.findIndex(([key]) => key === stage);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 26px' }}>
      {steps.map(([key, label], i) => (
        <React.Fragment key={key}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, color: i === ix ? 'var(--ft-ink)' : 'var(--ft-ink-3)' }}>
            <span style={{
              width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 600,
              background: i < ix ? 'var(--ft-green)' : i === ix ? 'var(--ft-dark)' : 'var(--ft-surface)',
              color: i <= ix ? '#fff' : 'var(--ft-ink-2)',
            }}>{i + 1}</span>
            {label}
          </div>
          {i < steps.length - 1 ? <div style={{ width: 26, height: 1, background: 'var(--ft-hairline)' }} /> : null}
        </React.Fragment>
      ))}
    </div>
  );
};

const ListTeamWizard = ({ type = 'orphan' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { leagues, leaguesLoading } = useShell();
  const { toast } = useShellUI();
  const isStartup = type === 'startup';
  const steps = isStartup
    ? [['league', 'League'], ['terms', 'Terms']]
    : [['league', 'League'], ['seat', 'Seat'], ['terms', 'Terms']];

  const [stage, setStage] = useState('league');
  const [joinLink, setJoinLink] = useState('');
  const [payoutStructure, setPayoutStructure] = useState('');
  const [rookiesIncluded, setRookiesIncluded] = useState(true);
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState(null);
  const [seat, setSeat] = useState(null);
  const [buyIn, setBuyIn] = useState('');
  const [yearsPaid, setYearsPaid] = useState([THIS_SEASON]);
  const [escrowType, setEscrowType] = useState('leaguesafe');
  const [escrowDetails, setEscrowDetails] = useState('');
  const [description, setDescription] = useState('');
  const [submitState, setSubmitState] = useState('idle');

  useEffect(() => {
    if (!league) return undefined;
    let active = true;
    setTeams(null);
    rateMyTradeAPI
      .getLeagueRosters(league.leagueId)
      .then((res) => { if (active) setTeams(res.data?.teams || []); })
      .catch(() => { if (active) setTeams([]); });
    return () => { active = false; };
  }, [league]);

  const pickLeague = (l) => { setLeague(l); setSeat(null); setStage(isStartup ? 'terms' : 'seat'); };
  const pickSeat = (t) => { setSeat(t); setStage('terms'); };

  const toggleYear = (y) => {
    setYearsPaid((prev) => (prev.includes(y) ? prev.filter((x) => x !== y) : [...prev, y].sort()));
  };

  const publish = async () => {
    setSubmitState('submitting');
    try {
      const common = {
        sleeperUsername: user.sleeperUsername,
        sleeperLeagueId: league.leagueId,
        buyIn: buyIn ? Number(buyIn) : 0,
        yearsPaid,
        escrowType,
        escrowDetails: escrowDetails.trim() || null,
        description: description.trim(),
      };
      const res = isStartup
        ? await startupMarketplaceAPI.create({
            ...common,
            joinLink: joinLink.trim() || null,
            payoutStructure: payoutStructure.trim() || null,
            rookiesIncluded,
          })
        : await orphanMarketplaceAPI.create({
            ...common,
            listingType: 'individual',
            sleeperRosterId: seat.rosterId,
          });
      const newId = res.data?.orphan?.id || res.data?.startup?.id || res.data?.id;
      const base = isStartup ? '/startup-marketplace' : '/orphan-marketplace';
      toast('Listing is live — applicants land on your listing page');
      navigate(newId ? `${base}/${newId}` : '/marketplaces' + (isStartup ? '?type=startup' : ''));
    } catch (err) {
      setSubmitState('idle');
      toast(err?.response?.data?.error || 'Could not publish — try again');
    }
  };

  const preview = (
    <div className="ft-railsec">
      <span className="ft-k">Live preview</span>
      <div className="ft-card" style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span
            className="ft-chip"
            style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}
          >
            {league ? leagueMeta(league) : 'League — to come'}
          </span>
          <span className="ft-chip brand" style={{ flex: 'none', whiteSpace: 'nowrap' }}>Your listing</span>
        </div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{league?.name || 'Pick a league'}</div>
        <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', margin: '2px 0 10px' }}>
          {isStartup ? 'Startup league — open spots' : seat ? `${seat.teamName} — open seat` : 'Pick the empty seat'}
        </div>
        {description ? (
          <div style={{ fontSize: 12.5, color: 'var(--ft-ink-2)', lineHeight: 1.45, marginBottom: 10 }}>{description}</div>
        ) : null}
        <div className="ft-pillrow">
          <span className="ft-chip dark">{buyIn ? `$${buyIn}` : 'Free'}</span>
          {yearsPaid.length ? <span className="ft-chip outline">Paid through {Math.max(...yearsPaid)}</span> : null}
          {escrowType ? <span className="ft-chip outline">{(ESCROWS.find(([, v]) => v === escrowType) || ['—'])[0]}</span> : null}
        </div>
      </div>
    </div>
  );

  return (
    <div className="ft-view">
      <Helmet><title>List a team — Marketplaces — Fantasy Taco</title></Helmet>

      <ShellRail>
        {preview}
        <div className="ft-railsec">
          <span className="ft-k">Why list here</span>
          <p className="ft-sub" style={{ fontSize: 13.5, marginTop: 8 }}>
            Every applicant arrives with their dynasty resume attached — record, titles, trade history.
          </p>
        </div>
      </ShellRail>

      <span className="ft-k brand">Marketplaces</span>
      <h1 className="ft-hero">{isStartup ? 'List a startup.' : 'List a team.'}</h1>
      <Stepper stage={stage} steps={steps} />

      {stage === 'league' && (
        <>
          <span className="ft-k">{isStartup ? 'Which league is filling up' : 'Which league has the open seat'}</span>
          {leaguesLoading ? (
            <div className="ft-loading">Loading your leagues…</div>
          ) : (
            <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {leagues.map((l) => (
                <button key={l.leagueId} className="ft-leaguecard" onClick={() => pickLeague(l)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <SleeperAvatar id={l.avatar} name={l.name} size={36} radius="10px" />
                    <div style={{ minWidth: 0 }}>
                      <div className="nm" style={{ marginBottom: 1 }}>{l.name}</div>
                      <div className="tm" style={{ marginBottom: 0 }}>{leagueMeta(l)} · {l.season}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div style={{ marginTop: 24 }}>
            <button className="ft-btn sm ghost" onClick={() => navigate('/marketplaces')}>Cancel</button>
          </div>
        </>
      )}

      {stage === 'seat' && (
        <>
          <span className="ft-k">Which seat is empty</span>
          {!teams ? (
            <div className="ft-loading">Pulling the league from Sleeper…</div>
          ) : (
            <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
              {teams.map((t) => {
                const prevLabel = league?.season && !Number.isNaN(Number(league.season))
                  ? String(Number(league.season) - 1)
                  : null;
                const record = formatTeamRecord(t.record, t.prevRecord, prevLabel);
                return (
                  <button key={t.rosterId} className="ft-leaguecard" onClick={() => pickSeat(t)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <SleeperAvatar id={t.avatar} name={t.teamName || t.ownerName} size={32} />
                      <div style={{ minWidth: 0 }}>
                        <div className="nm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.teamName}</div>
                        <div className="tm" style={{ marginBottom: 0 }}>
                          {t.ownerName}
                          {record ? ` · ${record}` : ''}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 24 }}>
            <button className="ft-btn sm ghost" onClick={() => setStage('league')}>Back</button>
          </div>
        </>
      )}

      {stage === 'terms' && league && (isStartup || seat) && (
        <>
          <div className="ft-card white" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap', maxWidth: 640 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{isStartup ? league.name : seat.teamName}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)' }}>{isStartup ? 'Startup league · pulled from Sleeper' : `${league.name} · pulled from Sleeper`}</div>
            </div>
            <div style={{ flex: 1 }} />
            <span className="ft-chip">{leagueMeta(league)}</span>
          </div>
          <p className="ft-sub" style={{ fontSize: 13, color: 'var(--ft-ink-3)', marginBottom: 4 }}>
            Roster and format came along automatically. You set the terms.
          </p>
          <div style={{ maxWidth: 560 }}>
            <label className="ft-k" style={{ display: 'block', margin: '18px 0 8px' }}>Buy-in (USD)</label>
            <input
              type="number"
              min="0"
              value={buyIn}
              onChange={(e) => setBuyIn(e.target.value)}
              placeholder="0 for a free league"
              style={{ width: '100%', background: 'var(--ft-surface)', border: 'none', borderRadius: 'var(--ft-r-input)', padding: '14px 16px', fontSize: 15, outline: 'none' }}
            />
            <label className="ft-k" style={{ display: 'block', margin: '18px 0 8px' }}>Dues already paid for</label>
            <div className="ft-pillrow">
              {YEAR_OPTIONS.map((y) => (
                <button key={y} className={`ft-fpill${yearsPaid.includes(y) ? ' on' : ''}`} onClick={() => toggleYear(y)}>{y}</button>
              ))}
            </div>
            <label className="ft-k" style={{ display: 'block', margin: '18px 0 8px' }}>Payment</label>
            <div className="ft-pillrow">
              {ESCROWS.map(([label, value]) => (
                <button key={label} className={`ft-fpill${escrowType === value ? ' on' : ''}`} onClick={() => setEscrowType(value)}>{label}</button>
              ))}
            </div>
            {escrowType === 'other' ? (
              <input
                value={escrowDetails}
                onChange={(e) => setEscrowDetails(e.target.value)}
                placeholder="How dues get paid"
                style={{ width: '100%', marginTop: 10, background: 'var(--ft-surface)', border: 'none', borderRadius: 'var(--ft-r-input)', padding: '14px 16px', fontSize: 15, outline: 'none' }}
              />
            ) : null}
            {isStartup && (
              <>
                <label className="ft-k" style={{ display: 'block', margin: '18px 0 8px' }}>Sleeper join link — optional</label>
                <input
                  value={joinLink}
                  onChange={(e) => setJoinLink(e.target.value)}
                  placeholder="https://sleeper.app/i/..."
                  style={{ width: '100%', background: 'var(--ft-surface)', border: 'none', borderRadius: 'var(--ft-r-input)', padding: '14px 16px', fontSize: 15, outline: 'none' }}
                />
                <label className="ft-k" style={{ display: 'block', margin: '18px 0 8px' }}>Payouts — optional</label>
                <input
                  value={payoutStructure}
                  onChange={(e) => setPayoutStructure(e.target.value)}
                  placeholder="60 / 30 / 10"
                  style={{ width: '100%', background: 'var(--ft-surface)', border: 'none', borderRadius: 'var(--ft-r-input)', padding: '14px 16px', fontSize: 15, outline: 'none' }}
                />
                <label className="ft-k" style={{ display: 'block', margin: '18px 0 8px' }}>Startup draft</label>
                <div className="ft-pillrow">
                  <button className={`ft-fpill${rookiesIncluded ? ' on' : ''}`} onClick={() => setRookiesIncluded(true)}>Rookies included</button>
                  <button className={`ft-fpill${!rookiesIncluded ? ' on' : ''}`} onClick={() => setRookiesIncluded(false)}>Separate rookie draft</button>
                </div>
              </>
            )}
            <label className="ft-k" style={{ display: 'block', margin: '18px 0 8px' }}>The pitch</label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What kind of manager should take this team"
              style={{ width: '100%', background: 'var(--ft-surface)', border: 'none', borderRadius: 'var(--ft-r-input)', padding: '14px 16px', fontSize: 15, outline: 'none', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 12, marginTop: 22 }}>
              <button className="ft-btn" disabled={submitState !== 'idle'} onClick={publish}>
                {submitState === 'submitting' ? 'Publishing…' : 'Publish listing'}
              </button>
              <button className="ft-btn ghost" onClick={() => setStage(isStartup ? 'league' : 'seat')}>Back</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListTeamWizard;
